import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { LicenseManager } from "@ag-grid-enterprise/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model/dist/cjs/clientSideRowModelModule";
import { SideBarModule } from "@ag-grid-enterprise/side-bar";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { GetRowIdParams, IFilterOptionDef } from "ag-grid-community";
import { CXAgent, CXExtension } from "./CxDashboard";

LicenseManager.setLicenseKey(
  "CompanyName=Resource Software International Ltd,LicensedApplication=Shadow,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=1,AssetReference=AG-032296,SupportServicesEnd=20_October_2023_[v2]_MTY5Nzc1NjQwMDAwMA==0c008d8dba7b9997038f1e1fee335d4a"
);

export interface CXAgentRowData extends CXExtension {
  firstName?: string;
  lastName?: string;
  agentId: number;
  id: string;
}

const aggFuncs = {
  mySum: (params: any) => {
    let sum = 0;
    params.values.forEach((value: any) => {
      const minutes = parseInt(value.split(":")[0], 10);
      const seconds = parseInt(value.split(":")[1], 10);
      const durationSeconds = minutes * 60 + seconds;
      sum += durationSeconds;
    });
    return `${Math.floor(Math.floor(sum / 60))
      .toString()
      .padStart(2, "0")}:${Math.floor(sum % 60)
      .toString()
      .padStart(2, "0")}`;
  },
  myAvg: (params: any) => {
    let count = 0;
    let sum = 0;
    params.values.forEach((value: any) => {
      const minutes = parseInt(value.split(":")[0], 10);
      const seconds = parseInt(value.split(":")[1], 10);
      const durationSeconds = minutes * 60 + seconds;
      if (durationSeconds > 0) {
        sum += durationSeconds;
        count += 1;
      }
    });
    if (count === 0) {
      return "00:00";
    }
    return `${Math.floor(Math.floor(sum / count / 60))
      .toString()
      .padStart(2, "0")}:${Math.floor((sum / count) % 60)
      .toString()
      .padStart(2, "0")}`;
  },
  myMax: (params: any) => {
    let max = 0;
    params.values.forEach((value: any) => {
      const minutes = parseInt(value.split(":")[0], 10);
      const seconds = parseInt(value.split(":")[1], 10);
      const durationSeconds = minutes * 60 + seconds;
      if (durationSeconds > max) {
        max = durationSeconds;
      }
    });
    return `${Math.floor(Math.floor(max / 60))
      .toString()
      .padStart(2, "0")}:${Math.floor(max % 60)
      .toString()
      .padStart(2, "0")}`;
  },
};

const textFilterParams = {
  filterOptions: [
    {
      displayKey: "textGreaterThen",
      displayName: "Greater Than",
      test: (filterVal: any, cellVal: any) => {
        // minutes:seconds (00:00) format

        if (/^(\d{1,}:\d{2})$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(5, "0");
          return cellVal > formattedFilterValue;
        }
        // minutes:seconds (00:0) format
        if (/^(\d{1,}:\d{1})$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(5, "0").concat("0");
          return cellVal > formattedFilterValue;
        }
        // minutes: (00:) format
        if (/^(\d{1,}:)$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(2, "0").concat("00");
          return cellVal > formattedFilterValue;
        }
        // minutes format
        if (/^\d{1,}$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(2, "0").concat(":00");
          return cellVal > formattedFilterValue;
        }
        return false;
      },
    },
    {
      displayKey: "textLessThen",
      displayName: "Less Than",
      test: (filterVal: any, cellVal: any) => {
        // minutes:seconds (00:00) format
        if (/^(\d{1,}:\d{2})$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(5, "0");
          return cellVal < formattedFilterValue;
        }
        // minutes:seconds (00:0) format
        if (/^(\d{1,}:\d{1})$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(5, "0").concat("0");
          return cellVal < formattedFilterValue;
        }
        // minutes: (00:) format
        if (/^(\d{1,}:)$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(2, "0").concat("00");
          return cellVal < formattedFilterValue;
        }
        // minutes format
        if (/^\d{1,}$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(2, "0").concat(":00");
          return cellVal < formattedFilterValue;
        }
        return false;
      },
    },
    "equals",
    "contains",
    "notContains",
  ] as IFilterOptionDef[],
};

const extensionPropGetter = (params: any) => {
  if (params === undefined || params === null) return "";
  return `${Math.floor(Math.floor(params || 0) / 60)
    .toString()
    .padStart(2, "0")}:${(Math.floor(params || 0) % 60)
    .toString()
    .padStart(2, "0")}`;
};

function CxQueueLeaderboardWaiting({
  fontSize,
  rowData,
  mode,
  local,
  updateLocal,
  myId,
  page,
  tab,
  setActiveFilterRef,
  selectedLeaderboard
}: {
  fontSize: string;
  rowData: CXExtension[] | undefined;
  mode: string;
  local: any;
  updateLocal: any;
  myId: string | undefined;
  page: string,
  tab: string,
  setActiveFilterRef: React.Dispatch<SetStateAction<{[colDef: string]: any}>>
  selectedLeaderboard: "summary" | "waiting" | "inProgress" | undefined
}) {
  const [gridReady, setGridReady] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact>(null);



  useEffect(() => {
    if (tab === 'cx-queues' && page === 'hud' && selectedLeaderboard === 'waiting') {
      if (gridRef.current && gridReady) {
        setActiveFilterRef(gridRef?.current?.api?.getFilterModel())

      }
    }
  }, [page, tab, gridReady, gridRef, selectedLeaderboard])


  const getRowNodeId = useCallback(
    (params: GetRowIdParams) =>
      `${params.data.extensionId}-${params.data.callId}`,
    []
  );

  const defaultColDef = useMemo(
    () => ({
      enablePivot: true,
      enableValue: true,
      sortable: true,
      resizable: true,
      filter: true,
      cellClass: " notarnslate ",
      cellClassRules: {
        small: () => fontSize === "small",
        medium: () => fontSize === "medium",
        large: () => fontSize === "large",
      },
    }),
    [fontSize]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: "extensionId",
        filter: "agTextColumnFilter",
        hide: true,
        filterParams: {
          valueGetter: (params: any) => params?.data?.extensionId || "",
        },
        valueGetter: (params: any) => params?.data?.extension || "--",
      },
      {
        field: "gateName",
        headerName: "Queue",
        filter: "agTextColumnFilter",
        pinned: true,
        filterParams: {
          valueGetter: (params: any) => params?.data?.gateName || "",
        },
      },
      {
        field: "gateGroupName",
        headerName: "Queue Group",
        filter: "agTextColumnFilter",
        filterParams: {
          valueGetter: (params: any) => params?.data?.gateGroupName || "",
        },
      },
      {
        field: "ani",
        headerName: "Caller Number",
        filter: "agTextColumnFilter",
      },
      {
        field: "dnis",
        headerName: "Callee Number",
        filter: "agTextColumnFilter",
      },
      {
        field: "callStart",
        headerName: "Waiting Since",
        // filter: "agTextColumnFilter",
      },
    ],
    []
  );

  const columnChanges = useCallback(() => {
    if (gridRef.current?.columnApi === undefined) {
      return;
    }
    const columnModel = gridRef.current.columnApi.getColumnState();
    localStorage.setItem(
      "cx-queue-waiting-leaderboard-columns",
      JSON.stringify(columnModel)
    );
  }, []);

  const onFilterChanged = useCallback(() => {
    if (gridRef?.current?.api) {
      const filterModel = gridRef.current.api.getFilterModel();
      localStorage.setItem(
        "cx-queue-waiting-leaderboard-filters",
        JSON.stringify(filterModel)
      );
      if (tab === 'cx-queues' && page === 'hud' && selectedLeaderboard === 'waiting') {
        if (gridRef.current && gridReady) {
          setActiveFilterRef(gridRef?.current?.api?.getFilterModel())
  
        }
      }
    }
  }, [page, tab, selectedLeaderboard]);

  useEffect(() => {
    if (gridReady) {
      const filterModel = localStorage.getItem(
        "cx-queue-waiting-leaderboard-filters"
      );
      const columnModel = localStorage.getItem(
        "cx-queue-waiting-leaderboard-columns"
      );
      if (
        gridRef?.current?.api !== undefined &&
        filterModel !== null &&
        filterModel !== undefined
      ) {
        gridRef.current!.api.setFilterModel(JSON.parse(filterModel));
      }
      if (
        gridRef?.current?.api !== undefined &&
        columnModel !== null &&
        columnModel !== undefined
      ) {
        gridRef.current!.columnApi.applyColumnState({
          state: JSON.parse(columnModel),
          applyOrder: true,
        });
      }
    }
  }, [gridReady]);

  return (
    <AgGridReact
      ref={gridRef}
      rowData={rowData}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowHeight={28}
      onGridReady={() => {
        setGridReady(true);
      }}
      pagination
      suppressScrollOnNewData
      containerStyle
      rowBuffer={50}
      aggFuncs={aggFuncs}
      suppressDragLeaveHidesColumns
      paginationAutoPageSize
      onFilterChanged={onFilterChanged}
      maintainColumnOrder
      sortingOrder={["asc", "desc"]}
      onColumnVisible={columnChanges}
      getRowId={getRowNodeId}
      onSortChanged={columnChanges}
      onColumnMoved={columnChanges}
      onColumnPinned={columnChanges}
      onColumnResized={columnChanges}
      getRowStyle={(params) => {
        if (params.node.rowPinned) {
          return { "font-weight": "bold" };
        }
        return { "font-weight": "400" };
      }}
      sideBar={{
        toolPanels: [
          "filters",
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: true,
              suppressColumnSelectAll: true,
              suppressColumnExpandAll: true,
            },
          },
        ],
      }}
    />
  );
}

export default CxQueueLeaderboardWaiting;
